// @ts-strict
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AppConstants } from "@app/utils";
import { LMCreateReportDialogService, LMReportPersonalizationDialogComponent } from "@app/components";
import { LMReportsCatalogStateService } from "@states/services/lm-reports-catalog-state.service";
import { SohoAlertDirective } from "ids-enterprise-ng";
import { LMContextService, LMFormatUtilService, LMUrlUtilService } from "@app/services";
import { Observable, Subject, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";

@Component({
  selector: 'lm-list-personalization-display',
  templateUrl: './lm-list-personalization-display.component.html',
  styleUrls: [ 'lm-list-personalization-display.component.scss' ],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush
  })

export class LMListPersonalizationDisplayComponent {
  get model(): LMListPersonalizationInfo  {
    return this._model;
  }

  @Input() set model(model: LMListPersonalizationInfo) {
    this._model = model;
    this.initialize();
  }

  @Input() appReport!: LMAppReport;

  reportCatalogEnabled!: boolean;
  pdfFontOffsetOptionDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-pdf-font-offset-option' } ];
  pageSizeOptionDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-page-size-option' } ];
  customFontOffsetSpinboxAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-pdf-font-offset' } ];
  customFontOffsetSpinBoxDataOptions = JSON.stringify({ min: -5, max: 20, attributes: this.customFontOffsetSpinboxAttributes });
  viewNameDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-view-name' } ];
  pageSizes: number[] = [];
  viewNames!: any[];
  private static readonly generalCategory = { value: '', label: 'General', selected: true };
  private _model!: LMListPersonalizationInfo;
  paperSizeList : Observable<Array<LMPdfPageSize>>;

  constructor(
      private lmContextService: LMContextService,
      private lmReportsCatalogStateService: LMReportsCatalogStateService,
      private lmFormatUtilService: LMFormatUtilService,
      private lmCreateReportDialogService: LMCreateReportDialogService,
      private lmUrlUtilService: LMUrlUtilService,
      private http: HttpClient
  ) {
    this.reportCatalogEnabled = this.lmContextService.getContext().enableReportCatalog || false;
    this.paperSizeList = this.getPdfSizeList();
    this.registerCustomRules();
  }

  registerCustomRules(){
    const invalidPageSize: any = {
      check: (value: any, field: any, grid: any) => {
        return this.validateCustomSize(value);
      },
      id: 'custom',
      type: 'error',
      message: Soho.Locale.translate('InvalidSize')
    };

    // @ts-ignore
    Soho.Validation.rules['invalidPageSize'] = invalidPageSize;
  }

  validateCustomSize(value: any) {
    if (!value?.trim()) {
      return true;
    }
    const pattern = new RegExp('^\\d{1,5}(\\.\\d{1,2})?$');
    return pattern.test(value);
  }

  onFontOffsetOptionChange(event: any) {
    const offsetOption = event?.['data'];
    this.updatePdfFontOffsetFromOption(offsetOption);
  }

  private initialize() {
    this.pageSizes = this.getPageSizes(this.model.showLines);
  }
  private updatePdfFontOffsetFromOption(offsetOption: string) {
    if (offsetOption === 'default') {
      this.model.fontOffset = 0;
    } else if (offsetOption === 'small' ) {
      this.model.fontOffset = -2;
    } else if (offsetOption === 'smaller' ) {
      this.model.fontOffset = -3;
    } else if (offsetOption === 'smallest') {
      this.model.fontOffset = -5;
    } else {
      this.model.fontOffset = 0;
    }
  }

  private getPageSizes(defaultPageSize?:  number): number[] {
    if (this.model.listType === 'tree' || this.model.listType === 'cube')
      return [];

    let pageSizeArray = AppConstants.PAGE_SIZES;
    if (defaultPageSize) {
      if (!pageSizeArray.includes(defaultPageSize)) {
        pageSizeArray = pageSizeArray.concat(defaultPageSize).sort((a, b) => a - b);
      }
    }

    return pageSizeArray;
  }

  viewNameSource = (response: any, searchTerm: any) => {
    response(this.viewNames, true);
  }

  onModuleChanged() {
    const reportState = this.lmReportsCatalogStateService.getCatalogReportState();

    this.model.category = ((reportState.categoriesByModule?.[this.model.module] || []) as LMAppReportOption[])?.find(cat => cat.selected)?.value || '';

    if (Object.keys(reportState.categoriesByModule || {}).length) {
      this.appReport.categories = [ LMListPersonalizationDisplayComponent.generalCategory, ...((reportState.categoriesByModule?.[this.model.module] || []) as LMAppReportOption[]) ];
      return;
    }

    this.lmCreateReportDialogService.getCategoriesByModule(this.model.businessClass, this.model.listName)
        .pipe(LMCreateReportDialogService.pipeOperatorHelper(this))
        .subscribe((res) => {
          if (res) {
            const categories = [ LMListPersonalizationDisplayComponent.generalCategory, ...(res.categoriesByModule?.[this.model.module] || []) ];
            this.appReport.categories = this.lmFormatUtilService.getUniqueListByKey(categories, 'value');

            reportState.categoriesByModule = res.categoriesByModule;
            this.lmReportsCatalogStateService.saveCatalogReportState(reportState);
          }
        });
  }

  onModuleSelect = (response: SohoDropDownResponseFunction) => {
    const reportState = this.lmReportsCatalogStateService.getCatalogReportState();

    if (reportState.modules?.length) {
      response(reportState.modules);
      return;
    }

    this.lmCreateReportDialogService.getModules(this.model.businessClass, this.model.listName)
        // .pipe(LMCreateReportDialogService.pipeOperatorHelper(this))
        .subscribe((res) => {
          // if (!res) {
          //   this.addTimeoutInlineAlert('lm-personalization-app-report-module');
          //   // fallback to previous value
          //   response(this.appReport.modules);
          // }

          if (res?.modules) {
            const modules = res.modules;
            response(modules);
            reportState.modules = modules;
            this.lmReportsCatalogStateService.saveCatalogReportState(reportState);
          }
        });
  }

  onCategorySelect = (response: SohoDropDownResponseFunction) => {
    const reportState = this.lmReportsCatalogStateService.getCatalogReportState();

    if (Object.keys(reportState.categoriesByModule || {}).length) {
      const categories = [ LMListPersonalizationDisplayComponent.generalCategory, ...(reportState.categoriesByModule?.[this.model.module] || []) ];
      response(categories);
      return;
    }

    this.lmCreateReportDialogService.getCategoriesByModule(this.model.businessClass, this.model.listName)
        // .pipe(LMCreateReportDialogService.pipeOperatorHelper(this))
        .subscribe((res) => {
          // if (!res) {
          //   // this.addTimeoutInlineAlert('lm-personalization-app-report-category');
          //   // fallback to previous value
          //   response(this.appReport.categories);
          // }

          if (res) {
            let categories = [ LMListPersonalizationDisplayComponent.generalCategory, ...(res.categoriesByModule?.[this.model.module] || []) ];
            categories = this.lmFormatUtilService.getUniqueListByKey(categories, 'value');
            
            if (this.model.category) {
              const selectedCategory: LMAppReportOption = categories.find(category => category.label === this.model.category.replace(/_/g, ' '));
              if (selectedCategory) {
                selectedCategory.selected = true;
              }
            }

            response(categories);

            reportState.categoriesByModule = res.categoriesByModule;
            this.lmReportsCatalogStateService.saveCatalogReportState(reportState);
          }
        });
  }

  private getPdfSizeList(): Observable<Array<LMPdfPageSize>> {
    const pdfPageSizeURL = `${this.lmUrlUtilService.getBaseUrl()}`
        + '/list/PDFPageSize.PDFPageSizeSelectList?pageop=load&pagesize=-1';

    return this.http
        .get(pdfPageSizeURL).pipe(
            map((response: any) => {
              const paperSizesList = this.getPdfSizeData(response.dataViewSet);
              if (paperSizesList && paperSizesList.length > 0) {
                this.model.paperSize = this.model.paperSize || paperSizesList[0].value;
              }
              return paperSizesList;
            }),
            catchError(error => this.handleError(error)));
  }

  private getPdfSizeData(dataViewSet: LMDataViewSet): Array<LMPdfPageSize>{
    const pageSizes: Array<LMPdfPageSize> = [];
    if (dataViewSet && dataViewSet?.data && dataViewSet?.data.length > 0) {
      dataViewSet.data.forEach(pdfSize => {
        pageSizes.push({value: pdfSize.fields['PDFPageSize'].value as string, description: pdfSize.representativeText});
      });
    }
    return pageSizes;
  }

  private handleError(error: Response) {
    return throwError(error || 'Server error');
  }

  // private addTimeoutInlineAlert(id: string): void {
  //   // const timeoutAlert = this.alerts.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === id);
  //   // timeoutAlert.addInlineMessage(Soho.Locale.translate('ListPersonalizationHTTPCallTimeout'), 'alert', true);
  // }

}

